import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { Infinity as InfinityIcon, Play, Loader2 } from "lucide-react";

const ATTACKS = [
  { key: "ransomware", label: "Ransomware Outbreak" },
  { key: "supply-chain", label: "Supply Chain Compromise" },
  { key: "insider", label: "Malicious Insider" },
];
const SEGMENTS = [
  { key: "prod", label: "Production" },
  { key: "corp", label: "Corporate" },
  { key: "ot", label: "OT / SCADA" },
];

const CyberTwin = () => {
  const [attack, setAttack] = useState("ransomware");
  const [segment, setSegment] = useState("prod");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const simulate = useCallback(async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/cyber-twin/simulate", { attack_path: attack, target_segment: segment });
      // animate steps
      for (let i = 0; i <= data.chain.length; i++) {
        await new Promise((r) => setTimeout(r, 500));
        setResult({ ...data, chain: data.chain.slice(0, i) });
      }
      setResult(data);
    } finally {
      setBusy(false);
    }
  }, [attack, segment]);

  useEffect(() => { simulate(); }, [simulate]);

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Platform / Twin"
        title="AI Cyber Twin"
        accent="pink"
        subtitle="Virtual replica of your environment. Run real-world attack chains in a safe sandbox to measure resilience."
        actions={
          <TButton data-testid="run-twin" icon={busy ? Loader2 : Play} onClick={simulate} disabled={busy} variant="accent">
            {busy ? "Simulating" : "Run Simulation"}
          </TButton>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="csx-card p-5 lg:col-span-1 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Attack Scenario</div>
            <div className="space-y-1.5">
              {ATTACKS.map((a) => (
                <button
                  key={a.key}
                  data-testid={`attack-${a.key}`}
                  onClick={() => setAttack(a.key)}
                  className={[
                    "w-full text-left text-xs uppercase tracking-widest p-2.5 border transition-all",
                    attack === a.key ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300" : "border-slate-800 text-slate-400 hover:border-slate-700"
                  ].join(" ")}
                >{a.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Target Segment</div>
            <div className="grid grid-cols-3 gap-1.5">
              {SEGMENTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSegment(s.key)}
                  className={[
                    "text-[10px] uppercase tracking-widest p-2 border transition-all",
                    segment === s.key ? "border-cyan-500 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400 hover:border-slate-700"
                  ].join(" ")}
                >{s.label}</button>
              ))}
            </div>
          </div>

          {result && (
            <div className="border-t border-slate-800 pt-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Resilience Score</div>
              <div className={`font-display text-6xl font-black ${result.resilience_score >= 60 ? "text-emerald-400" : "text-rose-400 text-glow-danger"}`}>
                {result.resilience_score}
              </div>
              <div className={`text-xs uppercase tracking-widest mt-1 ${result.resilience_score >= 60 ? "text-emerald-300" : "text-rose-300"}`}>
                {result.verdict}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 csx-card p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-4">
            <InfinityIcon className="h-3 w-3" /> Attack Chain
          </div>

          {!result || result.chain.length === 0 ? (
            <div className="text-xs text-slate-500 py-8 text-center">Click "Run Simulation" to begin.</div>
          ) : (
            <ol className="space-y-3">
              {result.chain.map((step) => {
                const blocked = step.status === "blocked" || step.status === "detected";
                const col = blocked ? "border-emerald-500/50 text-emerald-300" : "border-rose-500/50 text-rose-300";
                return (
                  <li key={step.step} data-testid={`twin-step-${step.step}`} className={`flex items-start gap-4 border-l-2 ${col} pl-4 py-2`}>
                    <div className="font-display text-2xl font-black text-slate-700 shrink-0 w-10">
                      0{step.step}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-bold text-slate-100">{step.action}</div>
                      <div className="text-xs text-slate-400 font-mono">node: {step.node} &middot; MITRE {step.ttp}</div>
                      <div className={`text-[10px] uppercase tracking-widest mt-1 ${col.split(" ")[1]}`}>
                        {step.status}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="mt-6 pt-4 border-t border-slate-800 text-[10px] text-slate-500 leading-relaxed">
            The Cyber Twin builds a digital mirror of your environment from Asset Discovery, applies live threat intel,
            then continuously runs adversary emulations to surface paths your defenders haven&apos;t seen yet.
          </div>
        </div>
      </div>
    </div>
  );
};

export default CyberTwin;
